/**
 * Тонкий HTTP-клиент для Lens API (`docs/api-design.md`).
 *
 * Задачи клиента:
 *  - `credentials: 'include'` — браузер сам шлёт cookie `lens.sid`;
 *  - единый формат ошибок `{ error: { code, message } }` разворачивается
 *    в typed-исключение `ApiError` с полями `status` и `code`, чтобы
 *    UI-компоненты могли делать узкие `catch` по коду (например, для
 *    локализации `invalid_credentials`);
 *  - авто-получение и повторное использование CSRF-токена для mutation-
 *    запросов; токен кешируется в памяти и сбрасывается на 403
 *    (sliding regeneration — фронт просто дёрнет /csrf ещё раз).
 *
 * Логика намеренно простая: это НЕ замена TanStack Query — это только
 * слой транспорта, поверх которого в M2 сядет react-query.
 */

export interface ApiErrorBody {
  code: string;
  message: string;
  details?: unknown;
  /** Backend-specific доп. поля (например, jiraErrors с push.service.ts). */
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.body = body;
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  method?: Method;
  body?: unknown;
  /**
   * Нужен ли CSRF-токен для этого запроса. По умолчанию — все методы
   * кроме GET/HEAD. Можно явно выключить (например, для самого
   * `/api/auth/csrf`, чтобы не поймать рекурсию).
   */
  csrf?: boolean;
  signal?: AbortSignal;
  /** Доп. заголовки запроса (мержатся последними). */
  headers?: Record<string, string>;
}

/**
 * Провайдер идентификатора канала «через браузер» (расширение Lens для Jira).
 * Когда режим доступа к Jira = extension, канал открыт и провайдер возвращает
 * его id — клиент подставляет заголовок X-Jira-Relay ко всем запросам, и Jira-
 * эндпоинты на сервере выполняют запросы через браузер пользователя.
 * В обычном (token) режиме провайдер не задан и заголовок не добавляется.
 */
let relayChannelIdProvider: (() => string | null) | null = null;

export function setJiraRelayChannelIdProvider(provider: (() => string | null) | null): void {
  relayChannelIdProvider = provider;
}

let cachedCsrfToken: string | null = null;

async function fetchCsrfToken(): Promise<string> {
  const res = await fetch('/api/auth/csrf', {
    method: 'GET',
    credentials: 'include',
  });
  if (!res.ok) {
    throw new ApiError(res.status, {
      code: 'csrf_fetch_failed',
      message: 'Не удалось получить CSRF-токен',
    });
  }
  const data = (await res.json()) as { token: string };
  return data.token;
}

async function ensureCsrfToken(): Promise<string> {
  if (cachedCsrfToken) return cachedCsrfToken;
  cachedCsrfToken = await fetchCsrfToken();
  return cachedCsrfToken;
}

/**
 * Сбрасывает кэш CSRF. Вызывается на 403 — тогда следующий запрос
 * сам получит свежий токен. Отдельно экспортирована, чтобы можно было
 * явно дропнуть токен после logout (сессия сменилась → токен устарел).
 */
export function resetCsrfToken(): void {
  cachedCsrfToken = null;
}

function needsCsrf(method: Method): boolean {
  return method !== 'GET';
}

/**
 * Актуальный CSRF-токен (с кэшем) для ручных `fetch` — например,
 * бинарных загрузок, где нужен `Response`, а не парсенный JSON.
 */
export async function getCsrfToken(): Promise<string> {
  return ensureCsrfToken();
}

export async function apiRequest<T>(url: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};
  let body: string | undefined;

  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const wantsCsrf = opts.csrf ?? needsCsrf(method);
  if (wantsCsrf) {
    headers['X-CSRF-Token'] = await ensureCsrfToken();
  }

  // Заголовок канала «через браузер» — если режим extension и канал открыт.
  const relayChannelId = relayChannelIdProvider?.();
  if (relayChannelId) {
    headers['X-Jira-Relay'] = relayChannelId;
  }
  if (opts.headers) {
    Object.assign(headers, opts.headers);
  }

  const doFetch = async (): Promise<Response> =>
    fetch(url, {
      method,
      headers,
      body,
      credentials: 'include',
      signal: opts.signal,
    });

  let res = await doFetch();

  // Если CSRF протух (сервер регенерировал токен, а кэш старый) —
  // сбрасываем кэш и делаем ровно один повтор. Без цикла — чтобы
  // не получить бесконечную серию на настоящей 403.
  if (res.status === 403 && wantsCsrf && cachedCsrfToken !== null) {
    resetCsrfToken();
    headers['X-CSRF-Token'] = await ensureCsrfToken();
    res = await doFetch();
  }

  if (res.status === 204) {
    return undefined as T;
  }

  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');

  if (!res.ok) {
    if (isJson) {
      const payload = (await res.json()) as { error?: ApiErrorBody };
      if (payload?.error) {
        throw new ApiError(res.status, payload.error);
      }
    }
    throw new ApiError(res.status, {
      code: 'unknown_error',
      message: `HTTP ${res.status}`,
    });
  }

  if (!isJson) {
    return undefined as T;
  }
  return (await res.json()) as T;
}
